/**
 * Logger utility for safe and controlled logging
 * Automatically filters out sensitive data in production
 */

// Determine current environment
const isProd = process.env.NODE_ENV === 'production';

// Log levels
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

// Default to only showing ERROR and WARN in production
const DEFAULT_PROD_LEVEL = LogLevel.WARN;
const DEFAULT_DEV_LEVEL = LogLevel.DEBUG;

// Get current log level
const getLogLevel = (): LogLevel => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL;
  if (envLevel) {
    const level = parseInt(envLevel, 10);
    if (!isNaN(level) && level >= 0 && level <= 3) {
      return level;
    }
  }
  return isProd ? DEFAULT_PROD_LEVEL : DEFAULT_DEV_LEVEL;
};

// Sensitive data patterns to redact (API keys, tokens, credentials)
const SENSITIVE_PATTERNS = [
  /Bearer\s+[A-Za-z0-9._-]+/gi,  // Bearer tokens
  /password["']?\s*:\s*["']?[^"',}]+/gi,  // Password fields
  /api[_-]?key["']?\s*:\s*["']?[^"',}]+/gi,  // API keys
  /token["']?\s*:\s*["']?[^"',}]+/gi,  // Tokens
  /auth["']?\s*:\s*["']?[^"',}]+/gi,  // Auth strings
  /secret["']?\s*:\s*["']?[^"',}]+/gi,  // Secret strings
];

// Sanitize object or string before logging
const sanitize = (data: any): any => {
  if (!isProd) return data; // Only sanitize in production
  
  if (typeof data === 'string') {
    let sanitized = data;
    SENSITIVE_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, match => {
        // Keep the key part but redact the value
        const keyPart = match.split(/:\s*/)[0];
        return `${keyPart}: "[REDACTED]"`;
      });
    });
    return sanitized;
  } else if (data && typeof data === 'object') {
    try {
      // Deep clone to avoid modifying original
      const clone = JSON.parse(JSON.stringify(data));
      
      // Recursively sanitize object properties
      const sanitizeObj = (obj: any) => {
        if (!obj || typeof obj !== 'object') return;
        
        Object.keys(obj).forEach(key => {
          // Redact known sensitive keys
          if (
            /token|key|secret|password|auth|credential/i.test(key) && 
            typeof obj[key] === 'string'
          ) {
            obj[key] = '[REDACTED]';
          } else if (typeof obj[key] === 'object') {
            sanitizeObj(obj[key]);
          }
        });
      };
      
      sanitizeObj(clone);
      return clone;
    } catch (e) {
      return '[Sanitization Error]';
    }
  }
  
  return data;
};

const currentLogLevel = getLogLevel();

// The logger object
const logger = {
  error: (message: string, ...args: any[]) => {
    if (currentLogLevel >= LogLevel.ERROR) {
      console.error(message, ...args.map(sanitize));
    }
  },
  
  warn: (message: string, ...args: any[]) => {
    if (currentLogLevel >= LogLevel.WARN) {
      console.warn(message, ...args.map(sanitize));
    }
  },
  
  info: (message: string, ...args: any[]) => {
    if (currentLogLevel >= LogLevel.INFO) {
      console.info(message, ...args.map(sanitize));
    }
  },
  
  debug: (message: string, ...args: any[]) => {
    if (currentLogLevel >= LogLevel.DEBUG) {
      console.debug(message, ...args.map(sanitize));
    }
  },
  
  // No filtering for critical errors that should always be logged
  critical: (message: string, ...args: any[]) => {
    console.error('[CRITICAL]', message, ...args.map(sanitize));
  }
};

export default logger; 